import type {DocsItem} from '@academy/components';

import {getPrimaryKidpreneursAcademyLink} from 'utils/getKidpreneursAcademyLink';
import {getPrimaryKidsCraftsLink} from 'utils/getKidsCraftsLink';

export const AGREEMENT_CLIENT_STATIC: DocsItem[] = [
    {
        title: "",
        content: [
          {
            html: `These User Agreement and Terms of Use (the “<strong>Terms</strong>”) govern your use of the websites and services operated by <strong>Ed Technologies Corp.</strong> (“<strong>Ed Technologies</strong>,” “<strong>we</strong>,” “<strong>us</strong>,” or “<strong>our</strong>”) and, where applicable, its <strong>affiliated companies and entities</strong>, including:`,
          },
          {
            html: `<ul class="list-inside list-disc mb-[1rem]">
              <li class="docs__item mb-[1rem]"><strong>KidEntrepreneurs.academy or Kidpreneurs.academy</strong> — educational classes and programs;</li>
              <li class="docs__item mb-[1rem]"><strong>KidsCrafts.shop</strong> — an educational marketplace where children can learn and practice business skills under parental or legal guardian supervision;</li>
              <li class="docs__item mb-[1rem]"><strong>OnlineSafetyReadiness.com</strong> — online safety education, training, assessments, and recommendations; and</li>
              <li class="docs__item"><strong>AfterSchoolVote.com</strong> — a platform for parents and guardians to vote on educational courses and programs.</li>
            </ul>`,
          },
          {
            html: `Together, these are the “<strong>Services</strong>.”`,
          },
          {
            html: `By accessing or using the Services, you agree to these Terms and our <a class="underline text-primary" href="/privacy-policy"><strong>Privacy Policy</strong></a>. If you do not agree, please do not use the Services.`,
          },
        ],
      },
      {
        title: "1. Eligibility and Accounts",
        content: [
          {
            html: `Our Services may be used by parents, legal guardians, students, children, educators, schools, school districts, educational institutions, and other authorized users, depending on the Service.`,
          },
          {
            html: `Where a child uses the Services, the required authorization or consent must be provided by a parent, legal guardian, school, school district, or other authorized organization, as applicable.`,
          },
          {
            html: `Parents and guardians are responsible for supervising their child's use of the Services and for activity conducted through accounts they create or manage.`,
          },
          {
            html: `You agree to provide accurate information, keep your account credentials secure, and notify us of unauthorized access.`,
          },
          {
            html: `Schools and organizations may manage or terminate accounts under their administration.`,
          },
        ],
      },
      {
        title: "2. KidsCrafts.shop",
        content: [
          {
            html: `KidsCrafts.shop is an educational marketplace designed to help children learn and practice business and sales skills.`,
          },
          {
            html: `Children may create listings and participate in marketplace activities only with appropriate parental or legal guardian supervision.`,
          },
          {
            html: `Parents or guardians are responsible for reviewing their child's activities and content. Listings must be appropriate, must not contain unnecessary personal information, and may not include illegal, dangerous, or prohibited goods or services.`,
          },
          {
            html: `Listings and other content may be reviewed or moderated by Ed Technologies or its designated partners. We may remove content or suspend accounts that violate these Terms or create a safety, legal, or security risk.`,
          },
        ],
      },
      {
        title: "3. AfterSchoolVote.com",
        content: [
          {
            html: `AfterSchoolVote.com is intended for parents and guardians to vote on and express their preferences regarding educational courses or programs they would like offered at their children's schools.`,
          },
          {
            html: `We may reject duplicate, fraudulent, automated, or unauthorized votes and may use voting results or aggregated preferences to evaluate interest in potential educational programs.`,
          },
        ],
      },
      {
        title: "4. OnlineSafetyReadiness.com",
        content: [
          {
            html: `OnlineSafetyReadiness.com provides online safety education, training, assessments, reports, and recommendations.`,
          },
          {
            html: `The Services may be used by students, parents, guardians, educators, schools, districts, and other authorized organizations.`,
          },
          {
            html: `Assessments, reports, and recommendations, including AI-generated recommendations, are intended to support and not replace the judgment of parents, educators, administrators, or qualified professionals.`,
          },
          {
            html: `We do not guarantee that the Services will identify every safety risk, accurately predict behavior, or produce a particular educational or safety outcome.`,
          },
        ],
      },
      {
        title: "5. Acceptable Use",
        content: [
          {
            list: {
              description: "You agree not to:",
              items: [
                "Use the Services for unlawful, fraudulent, harmful, or unauthorized purposes;",
                "Access another user's account or information without authorization;",
                "Submit false, misleading, illegal, or inappropriate content;",
                "Upload malware or interfere with the security or operation of the Services;",
                "Circumvent moderation or security measures;",
                "Copy, reverse engineer, modify, or exploit our technology without authorization; or",
                "Violate the rights, privacy, or safety of others.",
              ],
            },
          },
          {
            html: `We may monitor or review activity and content where appropriate and permitted by law, remove content, or suspend or terminate accounts when necessary to protect users or the Services.`,
          },
        ],
      },
      {
        title: "6. Payments",
        content: [
          {
            html: `Payments for classes, programs, products, or other Services may be processed through third-party payment providers. Parents are responsible for all payments.`,
          },
          {
            html: `You agree to provide accurate payment information and authorize applicable charges.`,
          },
          {
            html: `Refunds and cancellations are governed by the applicable purchase terms and applicable consumer protection laws. Nothing in these Terms limits mandatory consumer rights.`,
          },
        ],
      },
      {
        title: "7. User Content",
        content: [
          {
            html: `Some Services allow users to submit content, including product listings, images, descriptions, prices, comments, or other materials (“<strong>User Content</strong>”).`,
          },
          {
            html: `You remain responsible for User Content you submit or authorize and represent that you have the necessary rights and permissions.`,
          },
          {
            html: `You grant Ed Technologies a non-exclusive, worldwide, royalty-free license to host, store, display, reproduce, and use User Content as reasonably necessary to operate, maintain, secure, and provide the Services.`,
          },
          {
            html: `For KidsCrafts.shop, approved product listings may be visible to other users or the public.`,
          },
        ],
      },
      {
        title: "8. Intellectual Property",
        content: [
          {
            html: `The Services and their content, including educational materials, assessments, text, graphics, software, designs, logos, and trademarks, are owned by or licensed to Ed Technologies.`,
          },
          {
            html: `You may use them only as permitted by these Terms or applicable law. You may not copy, distribute, modify, sell, or commercially exploit our proprietary content or technology without permission.`,
          },
        ],
      },
      {
        title: "9. Privacy and Student Data",
        content: [
          {
            html: `Our collection and use of personal information are described in our <a class="underline text-primary" href="/privacy-policy"><strong>Privacy Policy</strong></a>.`,
          },
          {
            html: `When Services are provided through schools or educational organizations, Ed Technologies may process student information on their behalf and according to applicable agreements and law.`,
          },
          {
            html: `Where applicable, Ed Technologies may act as a service provider or “school official” under FERPA and comply with applicable Canadian privacy laws.`,
          },
          {
            html: `Parents, guardians, schools, and organizations are responsible for providing required authorizations or consents for children's or students' use of the Services.`,
          },
        ],
      },
      {
        title: "10. Third-Party Services",
        content: [
          {
            html: `The Services may use third-party providers for payments, hosting, analytics, communications, technology, AI, and other functions.`,
          },
          {
            html: `Third-party services may have their own terms and privacy policies. Ed Technologies is not responsible for third-party practices.`,
          },
        ],
      },
      {
        title: "11. Availability and Termination",
        content: [
          {
            html: `We do not guarantee that the Services will always be available, uninterrupted, secure, or error-free.`,
          },
          {
            html: `We may modify, suspend, or discontinue Services for technical, operational, security, legal, or business reasons.`,
          },
          {
            html: `We may suspend or terminate access if we reasonably believe that these Terms or applicable laws have been violated or that suspension is necessary to protect users or the Services.`,
          },
          {
            html: `Upon termination, access may be revoked and applicable content or account information may be deleted, subject to our <a class="underline text-primary" href="/privacy-policy">Privacy Policy</a>, applicable agreements, and legal requirements.`,
          },
        ],
      },
      {
        title: "12. Disclaimers and Limitation of Liability",
        content: [
          {
            html: `To the fullest extent permitted by law, the Services are provided on an <strong>“as is” and “as available”</strong> basis.`,
          },
          {
            html: `Ed Technologies does not guarantee the accuracy, completeness, availability, or suitability of the Services or AI-generated information.`,
          },
          {
            html: `To the fullest extent permitted by law, Ed Technologies Corp., its affiliates, officers, employees, contractors, and partners will not be liable for indirect, incidental, special, consequential, or punitive damages arising from your use of the Services.`,
          },
          {
            html: `Nothing in these Terms excludes or limits liability that cannot legally be excluded or limited.`,
          },
        ],
      },
      {
        title: "13. Indemnification",
        content: [
          {
            html: `To the fullest extent permitted by law, you agree to indemnify and hold harmless Ed Technologies Corp. and its affiliates, officers, employees, contractors, and partners from claims, damages, losses, liabilities, and reasonable expenses arising from your violation of these Terms, unlawful use of the Services, User Content you submit, or violation of applicable law or third-party rights.`,
          },
        ],
      },
      {
        title: "14. Changes to These Terms",
        content: [
          {
            html: `We may update these Terms from time to time. Updated Terms will be posted on the applicable websites.`,
          },
          {
            html: `Where required by law, we will provide additional notice of material changes.`,
          },
        ],
      },
      {
        title: "15. Governing Law",
        content: [
          {
            html: `These Terms are governed by the laws of the <strong>Province of British Columbia and the applicable laws of Canada</strong>, without regard to conflict-of-law principles.`,
          },
          {
            html: `For users outside British Columbia or Canada, applicable mandatory consumer protection, privacy, education, and child protection laws may also apply.`,
          },
          {
            html: `Nothing in these Terms is intended to waive rights or protections that cannot legally be waived.`,
          },
        ],
      },
      {
        title: "16. Contact Us",
        content: [
          {
            html: `If you have questions about these Terms or our Services, please contact us:<br /><br />
            <strong>Attn: Privacy Officer</strong><br />
            Email: <a class="underline text-primary" href="mailto:service@KidEntrepreneurs.academy">service@KidEntrepreneurs.academy</a>`,
          },
        ],
      },
];
